import { useSelector } from 'react-redux';
import { AppState } from '../../../client-redux/reducers';
import { Language } from '../../../languages/languageHandler';
import { useEffect, useState } from 'react';
import { fetchProducts, IProduct, ProductsResponse } from '../../../../libraries/helpdesk/product-management';
import Header from '../../../components/util/header';
import HelpdeskFilterBar, { HelpdeskFilterBarFieldsData } from '../../components/helpdesk-filterbar';
import { Group, Loader, Table } from '@mantine/core';
import { formatCurrAmount } from '../../../utils/script';
import PaginationComponent from '../../../components/pagination';
import ProductModal from './product-modal';
import { ICurrency } from '../../../../libraries/currencies';
import { useFitTableRows } from '../../../components/util/fit-table-rows-hook';
import * as Icon from '@tabler/icons-react';
import './styles.scss';

export enum MODE {
  READ = 'read',
  EDIT = 'edit',
  NEW = 'new'
}

const HelpdeskProductManagement = () => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const currencies = useSelector<AppState, ICurrency[]>(state => state.currencies.currencies);
  const controller = new AbortController();

  const [openDetails, setOpenDetails] = useState<boolean>(false);
  const [selectedProduct, setSelectedProduct] = useState<IProduct | undefined>(undefined);
  const [list, setList] = useState<ProductsResponse>();
  const [loading, setLoading] = useState<boolean>(false);
  const [noResults, setNoResults] = useState<boolean>(true);
  const [statusOperCode, setStatusOperCode] = useState<number | undefined>();
  const [currentPage, setCurrentPage] = useState<number>();
  const [totalRowsResponse, setTotalRowsResponse] = useState<number | undefined>();
  const [offset, setOffset] = useState<number>(0);
  const {
    limit = 10,
    setLimit,
    tableRef
  } = useFitTableRows({
    bufferRows: 1,
    minHeightForDynamic: 400
  });
  const [modalMode, setModalMode] = useState<MODE>(MODE.READ);
  const [filterBarData, setFilterBarData] = useState<HelpdeskFilterBarFieldsData>();

  useEffect(() => {
    if (filterBarData && limit) {
      loadProducts();
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBarData, limit, offset]);

  const handleFilterBarOnDataChange = (data: HelpdeskFilterBarFieldsData) => {
    setFilterBarData(data);
    changePage(1);
  };

  const loadProducts = () => {
    if (loading) {
      setNoResults(false);
      controller.abort();
    }
    setLoading(true);
    const shouldFetchTotalRows = offset === 0;

    fetchProducts(controller, offset, limit, shouldFetchTotalRows, filterBarData?.name, filterBarData?.productCode)
      .then(response => {
        if (response.statusOper.code === 0) {
          setList(response);
          if (shouldFetchTotalRows && typeof response.totalRows === 'number') {
            setTotalRowsResponse(response.totalRows);
          }
          setNoResults(false);
          setStatusOperCode(response.statusOper.code);
        } else {
          setNoResults(true);
          setStatusOperCode(response.statusOper.code);
        }
      })
      .catch(error => {
        if (error.name === 'AbortError') {
        } else {
        }
      })
      .finally(() => setLoading(false));
  };

  const handleRowClick = (product: IProduct) => {
    setSelectedProduct(product);
    setModalMode(MODE.READ);
    setOpenDetails(true);
  };

  const handleNewProductClick = () => {
    setSelectedProduct({
      productCode: '',
      um: '',
      description: '',
      hasComposition: false,
      hasSN: false,
      priceRef: 0,
      lastSupplier: 0,
      lastPrice: 0,
      ceCertificate: false,
      cbCertificate: false,
      averDeliTime: 0,
      devType: 0,
      price: 0,
      ctype: 0
    });
    setModalMode(MODE.NEW);
    setOpenDetails(true);
  };

  const handleEditClick = (product: IProduct) => {
    setSelectedProduct(product);
    setModalMode(MODE.EDIT);
    setOpenDetails(true);
  };

  const handleCheck = (isChecked: boolean) => {
    if (isChecked) {
      return <Icon.IconCheck className="status-ok" />;
    } else {
      return <Icon.IconSquareOff className="not-selected" />;
    }
  };

  const changePage = (page: number) => {
    const newOffset = (page - 1) * limit;
    setCurrentPage(page);
    setOffset(newOffset);
  };

  const resultsSelect = (newValue: number) => {
    const newLimit = Number(newValue);
    setLimit(newLimit);
    setCurrentPage(1);
    setOffset(0);
  };

  return (
    <div className="products">
      {selectedProduct && (
        <ProductModal
          mode={modalMode}
          isOpen={openDetails}
          hideBtn={() => setOpenDetails(false)}
          lang={lang}
          product={selectedProduct}
          currency={currencies[0]}
        />
      )}
      <Header pageTitle={lang.PRODUCT_MANAGEMENT} />

      <HelpdeskFilterBar
        onSearch={loadProducts}
        onDataChange={handleFilterBarOnDataChange}
        fieldsUsage={{
          name: true,
          productCode: true
        }}
        addButton={handleNewProductClick}
      />

      <Table.ScrollContainer minWidth={500} ref={tableRef}>
        <Table striped verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{lang.HELPDESK_PRODUCT_CODE}</Table.Th>
              <Table.Th>UM</Table.Th>
              <Table.Th>{lang.EQUIPMENT_DESCRIPTION}</Table.Th>
              <Table.Th>{lang.PRICE_REFERENCE}</Table.Th>
              <Table.Th>BM</Table.Th>
              <Table.Th>SN</Table.Th>
              <Table.Th>{lang.LAST_SUPPLIER}</Table.Th>
              <Table.Th>{lang.LAST_PRICE}</Table.Th>
              <Table.Th>{lang.AVERAGE_DELIVERY_TIME_MIN}</Table.Th>
              <Table.Th>{lang.CE_CERTIFICATE}</Table.Th>
              <Table.Th>{lang.CB_CERTIFICATE}</Table.Th>
              <Table.Th></Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {loading ? (
              <Table.Tr>
                <Table.Td colSpan={12}>
                  <Loader color="#016273" />
                </Table.Td>
              </Table.Tr>
            ) : (
              <>
                {noResults ? (
                  <Table.Tr>
                    <Table.Td colSpan={12} style={{ textAlign: 'center' }}>
                      {lang.GLOBAL_NO_RESULTS}
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  list?.products.map((product, index) => (
                    <Table.Tr key={index} style={{ cursor: 'pointer' }} onClick={() => handleRowClick(product)}>
                      <Table.Td>{product.productCode}</Table.Td>
                      <Table.Td>{product.um}</Table.Td>
                      <Table.Td>{product.description}</Table.Td>
                      <Table.Td>
                        {product.priceRef === 0 || product.priceRef === undefined
                          ? '-'
                          : formatCurrAmount(product.priceRef, currencies[0], true)}
                      </Table.Td>
                      <Table.Td>{handleCheck(product.hasComposition)}</Table.Td>
                      <Table.Td>{handleCheck(product.hasSN)}</Table.Td>
                      <Table.Td>
                        {product.lastSupplier === 0 || product.lastSupplier === undefined ? '-' : product.lastSupplier}
                      </Table.Td>
                      <Table.Td>
                        {product.lastPrice === 0 || product.lastPrice === undefined
                          ? '-'
                          : formatCurrAmount(product.lastPrice, currencies[0], true)}
                      </Table.Td>
                      <Table.Td>{product.averDeliTime === 0 ? '-' : product.averDeliTime}</Table.Td>
                      <Table.Td>{handleCheck(product.ceCertificate)}</Table.Td>
                      <Table.Td>{handleCheck(product.cbCertificate)}</Table.Td>
                      <Table.Td>
                        <Icon.IconEdit
                          stroke={2}
                          style={{ cursor: 'pointer' }}
                          onClick={e => {
                            e.stopPropagation();
                            handleEditClick(product);
                          }}
                        />
                      </Table.Td>
                    </Table.Tr>
                  ))
                )}
              </>
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      <Group>
        <PaginationComponent
          total={totalRowsResponse}
          statusOperCode={statusOperCode}
          itemsPerPage={limit}
          dynamicLimit={limit}
          currentPage={currentPage}
          onPageChange={changePage}
          onResultsChange={resultsSelect}
          offset={offset}
        />
      </Group>
    </div>
  );
};

export default HelpdeskProductManagement;
